import { resolveXlsxFileName } from '../utils/http/responseHelpers';
import qs from 'qs';
import type { FormTemplatesResponse } from '../models/TaskForm/TaskForm';
import {
  getTaskScenarioMeta,
  type ScenarioGroup,
} from '../models/Task/scenarioMapping';
import { apiClientWithApi, apiLoaderClientWithApi } from '../utils/http';
import { loadOutboundTaskTypeDictionary } from './taskTypeDictionary';

export type UploadFormData = {
  file: FormData;
  name?: string;
  from?: string;
  to?: string;
  templateId?: string;
};

export interface UploadResult {
  total: number;
  uploaded: number;
}

interface UploadApiResult {
  total?: number;
  uploaded?: number;
  itemsCount?: number;
  uploadedItemsCount?: number;
}

export const normalizeUploadResult = (
  result: UploadApiResult,
): UploadResult => ({
  total: result.total ?? result.itemsCount ?? 0,
  uploaded: result.uploaded ?? result.uploadedItemsCount ?? 0,
});

const getUploadEndpoint = (group: ScenarioGroup): string | null => {
  switch (group) {
    case 'slot':
      return 'task/slot/load/file';
    case 'informing':
      return 'task/notification/load/file';
    case 'dispensarization':
      return 'task/dispensarization/load/file';
    case 'form':
      return 'task/form/load/file';
    default:
      return null;
  }
};

const getTemplateEndpoint = (group: ScenarioGroup): string | null => {
  switch (group) {
    case 'slot':
      return 'task/slot/load/file/template';
    case 'informing':
      return 'task/notification/load/file/template';
    case 'dispensarization':
      return 'task/dispensarization/load/file/template';
    case 'form':
      return 'task/form/load/file/template';
    default:
      return null;
  }
};

const TEMPLATE_FALLBACK_FILE_NAMES: Record<ScenarioGroup, string> = {
  slot: 'Шаблон напоминание о приеме.xlsx',
  informing: 'Шаблон информирование клещи.xlsx',
  dispensarization: 'Шаблон диспансеризация.xlsx',
  form: 'Шаблон анкетирование.xlsx',
  unknown: 'Шаблон.xlsx',
};

const serializeRepeatedParams = (params: Record<string, unknown>): string =>
  qs.stringify(params, { arrayFormat: 'repeat' });

const getOutboundTaskTypes = async (): Promise<{
  types: string[];
  labels: Record<string, string>;
}> => {
  const data = await loadOutboundTaskTypeDictionary();
  if (!Array.isArray(data) || data.length === 0) {
    return { types: [], labels: {} };
  }

  const types: string[] = [];
  const labels: Record<string, string> = {};

  for (const item of data) {
    if (typeof item.key === 'string' && item.key) {
      types.push(item.key);
      if (typeof item.value === 'string' && item.value) {
        labels[item.key] = item.value;
      }
    }
  }

  return { types, labels };
};

const getOutboundFormTemplates = async (): Promise<FormTemplatesResponse> => {
  const response = await apiClientWithApi.get<FormTemplatesResponse>(
    'task/form/template',
    {
      params: {
        PageIndex: 0,
        PageSize: 100,
        sortQuery: JSON.stringify({
          sortKeySelector: 'name',
          sortOrder: 'Asc',
        }),
      },
      paramsSerializer: serializeRepeatedParams,
    },
  );
  return response.data;
};

const upload = async (
  formData: UploadFormData,
  taskType: string,
): Promise<UploadResult | null> => {
  const meta = getTaskScenarioMeta(taskType);
  const endpoint = getUploadEndpoint(meta.group);

  if (!endpoint) {
    return null;
  }

  const params: Record<string, unknown> = { type: taskType };
  if (meta.group === 'dispensarization') {
    if (formData.name) params.name = formData.name;
    if (formData.from) params.from = formData.from;
    if (formData.to) params.to = formData.to;
  } else if (meta.group === 'form') {
    if (formData.from) params.from = formData.from;
    if (formData.name) params.name = formData.name;
    if (formData.templateId) params.templateId = formData.templateId;
  }

  const response = await apiLoaderClientWithApi.post<UploadApiResult>(
    endpoint,
    formData.file,
    {
      params,
      paramsSerializer: serializeRepeatedParams,
    },
  );
  return normalizeUploadResult(response.data);
};

const getTemplate = async (
  taskType: string,
): Promise<{ blob: Blob; fileName: string } | null> => {
  const meta = getTaskScenarioMeta(taskType);
  const endpoint = getTemplateEndpoint(meta.group);

  if (!endpoint) {
    return null;
  }

  const response = await apiLoaderClientWithApi.get<Blob>(endpoint, {
    responseType: 'blob',
  });
  const fileName = resolveXlsxFileName(
    response.headers['content-disposition'],
    TEMPLATE_FALLBACK_FILE_NAMES[meta.group],
  );

  return { blob: new Blob([response.data]), fileName };
};

export const outgoingCallsUploadApi = {
  getOutboundTaskTypes,
  getOutboundFormTemplates,
  upload,
  getTemplate,
};
